import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import type { Designation } from "../types/designation.types";
import { createDesignation, updateDesignation } from "../services/DesignationService";

const schema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
});

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editing: Designation | null;
};

export function DesignationModal({ show, onClose, onSuccess, editing }: Props) {
  const isEdit = editing != null;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { name: editing?.name ?? "" },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        const payload = { name: values.name.trim() };
        const res = isEdit && editing
          ? await updateDesignation(editing.id, payload)
          : await createDesignation(payload);

        if (res.success) {
          toast.success(res.message);
          onSuccess();
          formik.resetForm();
          onClose();
        } else {
          toast.error(res.message ?? "Operation failed.");
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit ? "Failed to update designation" : "Failed to create designation"),
        );
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit designation" : "Add designation"}
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label className="form-label" htmlFor="desig-modal-name">
              Name
            </Label>
            <Input
              id="desig-modal-name"
              name="name"
              type="text"
              placeholder="e.g. Senior Engineer"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>

          <div className="d-flex justify-content-end gap-2 pt-2">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={formik.isSubmitting}>
              {isEdit ? "Save changes" : "Create designation"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
